import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import { Box, Button, useTheme, useMediaQuery, Typography, alpha } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormFieldsMenu from './components/FormFieldsMenu';
import SortableQuestionCard from './components/SortableQuestionCard';
import useEventDynamicFormStyles from './useEventDynamicForm';
import WarningMessage from './components/warningMessage';
import DeleteConfirmationDialog from './components/DeleteConfirmationDialog';
import { restrictToFirstScrollableAncestor, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { createTransportationPresetFields, TRANSPORT_MULTI_PARENT_TARGET_METAS, TRANSPORTATION_PRESET_TYPE } from './presets/transportationPreset';
import { ADDITIONAL_ATTENDEE_ALLOWED_SUB_FIELD_TYPES, ADDITIONAL_ATTENDEES_TYPE, createAdditionalAttendeesField, createDefaultAdditionalAttendeeSubFields, createDefaultAttendeeSubFieldOptions } from './presets/additionalAttendeesPreset';
const DynamicForm = ({ totalSteps, currentStep, step, eventId, watch, setValue, errors, eventHasRegistration, isPrimaryLanguage = true, allowStructureEdit = true, originalLanguageIds = [], translationLanguageCode, onStepNameError }) => {
    const { t, i18n } = useTranslation();
    const tStep = useMemo(() => (translationLanguageCode ? i18n.getFixedT(translationLanguageCode) : t), [translationLanguageCode, i18n, t]);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const styles = useEventDynamicFormStyles();
    const [addQuestionBtnAnchorEl, setAddQuestionBtnAnchorEl] = useState(null);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [fieldToDelete, setFieldToDelete] = useState(null);
    const sensor = useSensor(PointerSensor);
    const canChangeStructure = isPrimaryLanguage && allowStructureEdit;
    // Always pass the same number of sensors so useSensors' internal useMemo dependency array size stays constant
    const sensors = useSensors(sensor);
    const stepIndex = currentStep - 1;
    const stepNamePath = `steps.${stepIndex}.name`;
    const stepDescriptionPath = `steps.${stepIndex}.description`;
    const stepTitle = watch(stepNamePath);
    const stepDescription = watch(stepDescriptionPath);
    const fieldPath = `steps.${stepIndex}.formFields`;
    const formFields = (watch(fieldPath) || []).sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0));
    // Map all option IDs to their labels for cross-step/within-step branching visibility
    const allSteps = watch('steps') || [];
    const parentOptionLabelMap = useMemo(() => {
        const map = {};
        allSteps.forEach((s) => {
            (s.formFields || []).forEach((f) => {
                (f.options || []).forEach((opt) => {
                    map[opt.id] = opt.label;
                });
            });
        });
        return map;
    }, [allSteps]);
    // Map option IDs to their parent field labels (for branched-from tooltip when parentFieldLabel is missing e.g. from saved data)
    const parentFieldLabelMap = useMemo(() => {
        const map = {};
        allSteps.forEach((s) => {
            (s.formFields || []).forEach((f) => {
                (f.options || []).forEach((opt) => {
                    map[opt.id] = f.label ?? '';
                });
            });
        });
        return map;
    }, [allSteps]);
    // Selected event languages from meta form (array of objects or ids)
    const selectedLanguagesRaw = (watch('languageIds') || []);
    const selectedLanguages = selectedLanguagesRaw.map((opt) => ({
        id: typeof opt === 'object' && 'value' in opt ? String(opt.value) : String(opt),
        name: typeof opt === 'object' && 'label' in opt ? String(opt.label) : String(opt),
        flagUrl: typeof opt === 'object' && 'iconUrl' in opt && opt.iconUrl ? String(opt.iconUrl) : undefined
    }));
    // Check if event allows externals
    const externalsAllowed = watch('externalsAllowed') === true;
    const onClose = () => {
        setAddQuestionBtnAnchorEl(null);
    };
    const handleFieldTypeChange = ({ id, fieldParent, fieldChild, fieldType, descriptionForToolTip }) => {
        if (!canChangeStructure)
            return;
        // Prevent changing undertaking type if event has registrations and field already has a file from backend
        const targetField = formFields.find((f) => f.id === id);
        if (eventHasRegistration &&
            targetField?.fieldType === 'undertaking' &&
            targetField?.uploadedFileData) {
            return;
        }
        if (targetField?.fieldType === ADDITIONAL_ATTENDEES_TYPE) {
            return;
        }
        // Types that require options
        const optionTypes = ['radio', 'choice', 'checkbox-group', 'dropdown', 'multi-select', 'toggle'];
        const createToggleOptions = () => [
            {
                id: crypto.randomUUID(),
                label: t('events.toggleOptions.yes') || 'Yes',
                value: 1,
                isFromBackend: false
            },
            {
                id: crypto.randomUUID(),
                label: t('events.toggleOptions.no') || 'No',
                value: 0,
                isFromBackend: false
            }
        ];
        const updatedFields = formFields.map((f) => {
            if (f.id !== id)
                return f;
            let updatedField = {
                ...f,
                fieldType: fieldType,
                fieldParent: fieldParent,
                fieldChild: fieldChild,
                label: t('events.formFieldsMenu.newField', { fieldName: fieldChild || fieldParent }),
                descriptionForToolTip: descriptionForToolTip,
                // Changing type should drop any previous identity/unique flags
                isIdentity: false,
                isUniqueEmail: false,
                isUniquePhoneNumber: false
            };
            if (fieldType === 'toggle') {
                updatedField = {
                    ...updatedField,
                    options: createToggleOptions(),
                    isBranchingEnabled: false,
                    required: false
                };
            }
            // If type does NOT match optionTypes and options exist, empty options array
            if (!optionTypes.includes(fieldType) && f.options && f.options.length > 0) {
                updatedField.options = [];
            }
            return updatedField;
        });
        setValue(fieldPath, updatedFields);
    };
    const handleDuplicateField = (fieldId) => {
        if (!canChangeStructure)
            return;
        const index = formFields.findIndex((f) => f.id === fieldId);
        if (index === -1)
            return;
        const original = formFields[index];
        // Additional Attendees can only exist once per event (mirrors the guard in
        // handleFieldTypeChange) - the QuestionCard duplicate button is disabled for it too, but this
        // is the actual data-mutating path, so it must not rely solely on the UI staying in sync.
        if (original.fieldType === ADDITIONAL_ATTENDEES_TYPE) {
            return;
        }
        const duplicatedOptions = original.options?.map((opt) => ({
            ...opt,
            optionChainingShowField: []
        }));
        const duplicated = {
            ...original,
            id: crypto.randomUUID(),
            label: `${original.label} (Copy)`,
            options: duplicatedOptions,
            isBranchingEnabled: false,
            isFromBackend: false
        };
        const updated = [...formFields.slice(0, index + 1), duplicated, ...formFields.slice(index + 1)];
        // Recalculate fieldOrder for all fields after duplication
        const allSteps = watch('steps') || [];
        const currentStepIndex = allSteps.findIndex((s) => s.id === step.id);
        const fieldsInPreviousSteps = allSteps
            .slice(0, currentStepIndex)
            .reduce((total, s) => total + (s.formFields?.length || 0), 0);
        const updatedWithFieldOrder = updated.map((field, fieldIndex) => ({
            ...field,
            fieldOrder: fieldsInPreviousSteps + fieldIndex + 1
        }));
        setValue(fieldPath, updatedWithFieldOrder);
    };
    const handleDeleteField = (fieldId) => {
        if (!canChangeStructure)
            return;
        const field = formFields.find((f) => f.id === fieldId);
        if (!field)
            return;
        // Prevent deleting undertaking with existing file if event has registrations
        if (eventHasRegistration && field.fieldType === 'undertaking' && field.uploadedFileData) {
            return;
        }
        // Check if field is from backend
        if (field.isFromBackend) {
            setFieldToDelete(field);
            setDeleteConfirmationOpen(true);
        }
        else {
            // Delete immediately for frontend-created fields
            performFieldDeletion(fieldId);
        }
    };
    const performFieldDeletion = (fieldId) => {
        const updated = formFields.filter((f) => f.id !== fieldId);
        // Recalculate fieldOrder for all remaining fields after deletion
        const allSteps = watch('steps') || [];
        const currentStepIndex = allSteps.findIndex((s) => s.id === step.id);
        const fieldsInPreviousSteps = allSteps
            .slice(0, currentStepIndex)
            .reduce((total, s) => total + (s.formFields?.length || 0), 0);
        const updatedWithFieldOrder = updated.map((field, fieldIndex) => ({
            ...field,
            fieldOrder: fieldsInPreviousSteps + fieldIndex + 1
        }));
        setValue(fieldPath, updatedWithFieldOrder);
    };
    const handleConfirmDelete = () => {
        if (fieldToDelete) {
            performFieldDeletion(fieldToDelete.id);
            setDeleteConfirmationOpen(false);
            setFieldToDelete(null);
        }
    };
    const handleCancelDelete = () => {
        setDeleteConfirmationOpen(false);
        setFieldToDelete(null);
    };
    const handleToggleRequired = (fieldId) => {
        if (!canChangeStructure)
            return;
        const targetField = formFields.find((f) => f.id === fieldId);
        if (targetField?.fieldType === 'toggle')
            return;
        const updated = formFields.map((f) => (f.id === fieldId ? { ...f, required: !f.required } : f));
        setValue(fieldPath, updated);
    };
    const handleUndertakingFilesChange = (fieldId, filesByLanguage) => {
        if (!canChangeStructure)
            return;
        const selectedLanguageIds = selectedLanguages.map((l) => l.id);
        const updated = formFields.map((f) => {
            if (f.id !== fieldId)
                return f;
            // Get previous state to compare
            const previousFilesByLanguage = f.undertakingFilesByLanguage || {};
            const previousUploadedFilesByLanguage = f.undertakingUploadedFilesByLanguage || {};
            // Clear uploaded files only for languages where filesByLanguage[langId] is explicitly an empty array []
            // An empty array means files were removed for that language
            // undefined means that language never had new files, so don't clear uploaded files
            const updatedUploadedFilesByLanguage = { ...previousUploadedFilesByLanguage };
            selectedLanguageIds.forEach((langId) => {
                // Check if filesByLanguage[langId] is explicitly an empty array (files were removed)
                const filesWereRemoved = Array.isArray(filesByLanguage?.[langId]) && filesByLanguage[langId].length === 0;
                // Only clear uploaded files if files were explicitly removed (empty array) for this language
                if (filesWereRemoved && updatedUploadedFilesByLanguage[langId]) {
                    delete updatedUploadedFilesByLanguage[langId];
                }
            });
            const next = {
                ...f,
                undertakingFilesByLanguage: filesByLanguage,
                undertakingUploadedFilesByLanguage: Object.keys(updatedUploadedFilesByLanguage).length > 0
                    ? updatedUploadedFilesByLanguage
                    : undefined
            };
            // Check both newly selected files AND uploaded files from server
            const allLanguagesHaveFiles = selectedLanguageIds.every((langId) => {
                const newFiles = filesByLanguage?.[langId];
                const uploadedFiles = next.undertakingUploadedFilesByLanguage?.[langId];
                const hasNewFiles = Array.isArray(newFiles) && newFiles.length > 0;
                const hasUploadedFiles = Array.isArray(uploadedFiles) && uploadedFiles.length > 0;
                return hasNewFiles || hasUploadedFiles;
            });
            if (f.fieldType === 'undertaking' && f.required) {
                next.error = allLanguagesHaveFiles ? undefined : 'validations.required';
                next.errorField = f.label || 'Undertaking document';
            }
            return next;
        });
        setValue(fieldPath, updated);
    };
    const handleUpdateLabel = (fieldId, newLabel, isFromSuggestion, suggestionId) => {
        const updated = formFields.map((f) => {
            if (f.id !== fieldId)
                return f;
            // Validate: label cannot be empty
            const trimmedLabel = newLabel?.trim() || '';
            const isEmpty = trimmedLabel === '';
            let updatedField = {
                ...f,
                label: newLabel,
                isIdentity: isFromSuggestion,
                // Set error if empty, clear error if not empty
                error: isEmpty ? 'validations.required' : undefined,
                errorField: isEmpty ? t('labels.fieldLabel') || 'Field label' : undefined
            };
            // When a label comes from a suggestion, set unique email/phone flags based on suggestion id
            if (isFromSuggestion && suggestionId) {
                const suggestionKey = suggestionId.toLowerCase();
                if (suggestionKey === 'email' && f.fieldType === 'email') {
                    updatedField = {
                        ...updatedField,
                        isUniqueEmail: true,
                        isUniquePhoneNumber: false
                    };
                }
                else if (suggestionKey === 'phone' && f.fieldType === 'phone') {
                    updatedField = {
                        ...updatedField,
                        isUniquePhoneNumber: true,
                        isUniqueEmail: false
                    };
                }
            }
            return updatedField;
        });
        setValue(fieldPath, updated);
    };
    const handleUpdateDescriptionForTooltip = (fieldId, newDescription) => {
        const updated = formFields.map((f) => {
            if (f.id !== fieldId)
                return f;
            let updatedField = {
                ...f,
                descriptionForToolTip: newDescription
                // Set error if empty, clear error if not empty
                // error: isEmpty ? 'validations.required' : undefined,
                // errorField: isEmpty ? t('labels.fieldLabel') || 'Field label' : undefined
            };
            return updatedField;
        });
        setValue(fieldPath, updated);
    };
    // Collect all labels currently marked as identity so suggestions can be hidden globally
    const usedIdentityLabels = (formFields || [])
        .filter((f) => f.isIdentity && f.label)
        .map((f) => String(f.label));
    const handleAddOption = (fieldId) => {
        if (!canChangeStructure)
            return;
        const updated = formFields.map((f) => {
            if (f.id !== fieldId)
                return f;
            const existingValues = (f.options || []).map((opt) => opt.value ?? 0);
            const newOption = {
                id: crypto.randomUUID(),
                label: `Option ${(f.options?.length || 0) + 1}`,
                // Math.max, not length+1: after a removal leaves a gap (see handleRemoveOption, which no
                // longer re-indexes), length+1 could reuse a value a still-unsaved branching link already
                // points at (parentOptionValue - the only way to reference an option that doesn't have a
                // real backend id yet), silently retargeting that link to the wrong option.
                value: Math.max(0, ...existingValues) + 1,
                isFromBackend: false
            };
            return {
                ...f,
                options: [...(f.options || []), newOption]
            };
        });
        setValue(fieldPath, updated);
    };
    const handleUpdateOptionLabel = (fieldId, optionId, label) => {
        const updated = formFields.map((f) => {
            if (f.id !== fieldId)
                return f;
            // Validate: label cannot be empty
            const trimmedLabel = label?.trim() || '';
            const isEmpty = trimmedLabel === '';
            return {
                ...f,
                options: f.options?.map((opt) => opt.id === optionId
                    ? {
                        ...opt,
                        label,
                        // Set error if empty, clear error if not empty
                        error: isEmpty ? 'validations.required' : undefined,
                        errorField: isEmpty ? t('labels.fieldLabel') || 'Field label' : undefined
                    }
                    : opt)
            };
        });
        setValue(fieldPath, updated);
    };
    const handleRemoveOption = (fieldId, optionId) => {
        if (!canChangeStructure)
            return;
        // Deliberately NOT re-indexing survivors' values anymore (removed a `.map((opt, idx) => ({
        // ...opt, value: idx + 1 }))` here) - a still-unsaved branching link on another field can only
        // reference an unsaved option by its current `value` (see handleOptionShowFieldChange), so
        // renumbering every survivor here silently invalidated any such link, and on save the backend
        // could no longer find "the option with that value" (or found the wrong one) - surfacing as
        // "Unable to resolve branching option. Ensure option values are unique within the parent field."
        // Values only need to stay unique within the field, not sequential/gapless, so leaving gaps here
        // is fine - handleAddOption already computes new values via Math.max(...) + 1 to avoid collisions.
        const updated = formFields.map((f) => f.id === fieldId
            ? {
                ...f,
                options: f.options?.filter((opt) => opt.id !== optionId)
            }
            : f);
        setValue(fieldPath, updated);
    };
    const handleOptionShowFieldChange = (fieldId, optionId, values) => {
        if (!canChangeStructure)
            return;
        const allowsMultipleParentOptions = (field) => {
            const note = field?.fieldAdditionalNote || '';
            return TRANSPORT_MULTI_PARENT_TARGET_METAS.has(note.trim());
        };
        const formFieldById = new Map(formFields.map((f) => [f.id, f]));
        let updatedFields = formFields.map((f) => f.id === fieldId
            ? {
                ...f,
                options: f.options?.map((opt) => opt.id === optionId ? { ...opt, optionChainingShowField: values } : opt)
            }
            : f);
        // These preset targets may be shown after multiple parent options.
        // For all other target questions, keep single-option attachment by removing duplicates elsewhere.
        const singleParentTargetIds = values.filter((targetId) => {
            const targetField = formFieldById.get(targetId);
            return !allowsMultipleParentOptions(targetField);
        });
        if (singleParentTargetIds.length > 0) {
            updatedFields = updatedFields.map((field) => ({
                ...field,
                options: field.options?.map((opt) => {
                    if (field.id === fieldId && opt.id === optionId)
                        return opt;
                    const currentTargets = opt.optionChainingShowField || [];
                    const nextTargets = currentTargets.filter((id) => !singleParentTargetIds.includes(id));
                    return nextTargets.length === currentTargets.length
                        ? opt
                        : { ...opt, optionChainingShowField: nextTargets };
                })
            }));
        }
        // Recompute parent linkage from current option-level routes.
        const routeRefsByChild = {};
        updatedFields.forEach((parentField) => {
            (parentField.options || []).forEach((parentOption) => {
                (parentOption.optionChainingShowField || []).forEach((childId) => {
                    if (!routeRefsByChild[childId])
                        routeRefsByChild[childId] = [];
                    routeRefsByChild[childId].push({ parentField, parentOption });
                });
            });
        });
        const recomputedFields = updatedFields.map((childField) => {
            const refs = routeRefsByChild[childField.id] || [];
            if (refs.length === 0) {
                return {
                    ...childField,
                    parentOptionId: null,
                    parentOptionIds: [],
                    parentFieldId: null,
                    parentFieldLabel: null,
                    parentOptionLabel: null,
                    parentOptionValue: null
                };
            }
            const firstRef = refs[0];
            const parentOptionIds = Array.from(new Set(refs
                .filter((r) => r.parentField.fieldType !== 'pickupLocation' &&
                r.parentOption.isFromBackend &&
                Boolean(r.parentOption.id))
                .map((r) => String(r.parentOption.id))));
            const isExistingSavedOption = firstRef.parentField.fieldType !== 'pickupLocation' &&
                firstRef.parentOption.isFromBackend &&
                Boolean(firstRef.parentOption.id);
            return {
                ...childField,
                parentOptionId: isExistingSavedOption ? (firstRef.parentOption.id ?? null) : null,
                parentOptionIds,
                parentFieldId: isExistingSavedOption ? null : (firstRef.parentField.id ?? null),
                parentFieldLabel: isExistingSavedOption ? null : (firstRef.parentField.label ?? null),
                parentOptionLabel: isExistingSavedOption ? null : (firstRef.parentOption.label ?? null),
                parentOptionValue: isExistingSavedOption ? null : (firstRef.parentOption.value ?? null)
            };
        });
        setValue(fieldPath, recomputedFields);
    };
    const handleFileChange = (fieldId, file) => {
        if (!canChangeStructure)
            return;
        const updated = formFields.map((f) => {
            if (f.id === fieldId) {
                if (file) {
                    // File uploaded - clear error
                    return {
                        ...f,
                        fileData: file,
                        uploadedFileData: f.uploadedFileData,
                        error: undefined // Clear error when file is uploaded
                    };
                }
                else {
                    // File removed - set error if it's a required undertaking field
                    const errorKey = f.fieldType === 'undertaking' && f.required ? 'validations.required' : undefined;
                    return {
                        ...f,
                        fileData: file,
                        uploadedFileData: undefined,
                        error: errorKey,
                        errorField: f.label || 'Undertaking document' // Store field name for translation
                    };
                }
            }
            return f;
        });
        setValue(fieldPath, updated);
    };
    const handleUndertakingTextChange = (fieldId, toggleLabel, alertNote) => {
        if (!canChangeStructure)
            return;
        const updated = formFields.map((f) => {
            if (f.id === fieldId) {
                return {
                    ...f,
                    undertakingToggleLabel: toggleLabel,
                    undertakingAlertNote: alertNote
                };
            }
            return f;
        });
        setValue(fieldPath, updated);
    };
    const handleShowFieldChange = (fieldId, value) => {
        if (!canChangeStructure)
            return;
        const updated = formFields.map((f) => (f.id === fieldId ? { ...f, showField: value } : f));
        setValue(fieldPath, updated);
    };
    const handleBranchingEnabledChange = (fieldId, isEnabled) => {
        if (!canChangeStructure)
            return;
        const updated = formFields.map((f) => f.id === fieldId ? { ...f, isBranchingEnabled: isEnabled } : f);
        setValue(fieldPath, updated);
    };
    const handleDragEnd = (event) => {
        if (!canChangeStructure)
            return;
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = formFields.findIndex((f) => f.id === active.id);
            const newIndex = formFields.findIndex((f) => f.id === over?.id);
            const reorderedFields = arrayMove(formFields, oldIndex, newIndex);
            // Get all steps to calculate global field order
            const allSteps = watch('steps') || [];
            const currentStepIndex = allSteps.findIndex((s) => s.id === step.id);
            const fieldsInPreviousSteps = allSteps
                .slice(0, currentStepIndex)
                .reduce((total, s) => total + (s.formFields?.length || 0), 0);
            // Collect field IDs that must have their parent linkage cleared (deselected due to reorder)
            const childIdsToClearLinkage = new Set();
            // After reorder, an option can only show fields that appear *after* it. Remove invalid
            // targets and set to [] when none remain, so the branched indicator is cleared.
            const updatedFields = reorderedFields.map((field, index) => {
                const validTargetIds = new Set(reorderedFields.slice(index + 1).map((f) => f.id));
                let optionsUpdated = field.options;
                if (field.options?.length) {
                    optionsUpdated = field.options.map((opt) => {
                        const current = opt.optionChainingShowField ?? [];
                        const newValues = current.filter((v) => validTargetIds.has(v));
                        const finalValues = newValues.length === 0 ? [] : newValues;
                        const removedIds = current.filter((v) => !validTargetIds.has(v));
                        removedIds.forEach((id) => childIdsToClearLinkage.add(id));
                        return {
                            ...opt,
                            optionChainingShowField: finalValues
                        };
                    });
                }
                return {
                    ...field,
                    fieldOrder: fieldsInPreviousSteps + index + 1,
                    options: optionsUpdated ?? field.options
                };
            });
            // Clear parent linkage on any field that was removed from an option's list
            const finalFields = updatedFields.map((f) => childIdsToClearLinkage.has(f.id)
                ? {
                    ...f,
                    parentOptionId: null,
                    parentOptionIds: [],
                    parentFieldId: null,
                    parentFieldLabel: null,
                    parentOptionValue: null
                }
                : f);
            setValue(fieldPath, finalFields);
        }
    };
    const handleCompanyDropdownFieldToggle = (fieldId, subFieldId, toggleType) => {
        if (!canChangeStructure)
            return;
        // Prevent any changes to fullName and companyName fields
        if (subFieldId === 'fullName' || subFieldId === 'companyName') {
            return; // Do nothing - these fields must always be enabled and required
        }
        const updated = formFields.map((f) => {
            if (f.id !== fieldId || f.fieldType !== 'companyDropdown')
                return f;
            return {
                ...f,
                companyDropdownFields: f.companyDropdownFields?.map((subField) => subField.id === subFieldId
                    ? { ...subField, [toggleType]: !subField[toggleType] }
                    : subField)
            };
        });
        setValue(fieldPath, updated);
    };
    const updateAdditionalAttendeesField = (fieldId, updater) => {
        const updated = formFields.map((field) => {
            if (field.id !== fieldId || field.fieldType !== ADDITIONAL_ATTENDEES_TYPE)
                return field;
            return updater(field);
        });
        setValue(fieldPath, updated);
    };
    const handleMaxAdditionalAttendeesChange = (fieldId, max) => {
        if (!canChangeStructure)
            return;
        updateAdditionalAttendeesField(fieldId, (field) => ({
            ...field,
            maxAdditionalAttendees: max
        }));
    };
    const handleMaxAdditionalAttendeesReachedMessageChange = (fieldId, message) => {
        updateAdditionalAttendeesField(fieldId, (field) => ({
            ...field,
            maxAdditionalAttendeesReachedMessage: message
        }));
    };
    const handleAdditionalAttendeeSubFieldLabelUpdate = (fieldId, subFieldId, newLabel) => {
        updateAdditionalAttendeesField(fieldId, (field) => ({
            ...field,
            additionalAttendeeFields: field.additionalAttendeeFields?.map((subField) => subField.id === subFieldId ? { ...subField, label: newLabel } : subField)
        }));
    };
    const handleAdditionalAttendeeSubFieldRequiredChange = (fieldId, subFieldId, required) => {
        if (!canChangeStructure)
            return;
        updateAdditionalAttendeesField(fieldId, (field) => ({
            ...field,
            additionalAttendeeFields: field.additionalAttendeeFields?.map((subField) => subField.id === subFieldId ? { ...subField, required } : subField)
        }));
    };
    const handleAdditionalAttendeeSubFieldTypeChange = (fieldId, subFieldId, fieldType, fieldParent, fieldChild) => {
        if (!canChangeStructure)
            return;
        updateAdditionalAttendeesField(fieldId, (field) => ({
            ...field,
            additionalAttendeeFields: field.additionalAttendeeFields?.map((subField) => {
                // Mandatory sub-questions (first/last name, email) keep a fixed type so guest name
                // resolution (Transport, and later per-guest email) can rely on them.
                if (subField.id !== subFieldId || subField.role)
                    return subField;
                const generatedField = generateFieldConfig({
                    parentId: '',
                    parentLabel: fieldParent,
                    childId: '',
                    childLabel: fieldChild,
                    type: fieldType,
                    fieldOrder: subField.fieldOrder || 1,
                    descriptionForToolTip: ''
                });
                const nextField = {
                    ...subField,
                    fieldType,
                    fieldParent,
                    fieldChild,
                    label: generatedField.label,
                    options: createDefaultAttendeeSubFieldOptions(fieldType, (key) => String(t(key)))
                };
                return nextField;
            })
        }));
    };
    const handleAdditionalAttendeeSubFieldAdd = (fieldId, { parent, child, type, description }) => {
        if (!canChangeStructure)
            return;
        if (!ADDITIONAL_ATTENDEE_ALLOWED_SUB_FIELD_TYPES.includes(type))
            return;
        updateAdditionalAttendeesField(fieldId, (field) => {
            const subFields = field.additionalAttendeeFields || [];
            const generatedField = generateFieldConfig({
                parentId: parent.id,
                parentLabel: parent.label,
                childId: child?.id,
                childLabel: child?.label,
                type,
                fieldOrder: subFields.length + 1,
                descriptionForToolTip: description
            });
            const newSubField = {
                id: generatedField.id,
                label: generatedField.label,
                fieldType: generatedField.fieldType,
                fieldParent: generatedField.fieldParent || parent.label,
                fieldChild: generatedField.fieldChild,
                fieldOrder: subFields.length + 1,
                required: false,
                options: createDefaultAttendeeSubFieldOptions(generatedField.fieldType, (key) => String(t(key)), generatedField.options)
            };
            return {
                ...field,
                additionalAttendeeFields: [...subFields, newSubField]
            };
        });
    };
    const handleAdditionalAttendeeSubFieldRemove = (fieldId, subFieldId) => {
        if (!canChangeStructure)
            return;
        updateAdditionalAttendeesField(fieldId, (field) => {
            const target = (field.additionalAttendeeFields || []).find((sf) => sf.id === subFieldId);
            if (target?.role)
                return field; // mandatory sub-questions can't be removed
            const remaining = (field.additionalAttendeeFields || []).filter((subField) => subField.id !== subFieldId);
            if (remaining.length === 0)
                return field;
            return {
                ...field,
                additionalAttendeeFields: remaining.map((subField, index) => ({
                    ...subField,
                    fieldOrder: index + 1
                }))
            };
        });
    };
    const handleAdditionalAttendeeSubFieldsReorder = (fieldId, activeId, overId) => {
        if (!canChangeStructure)
            return;
        updateAdditionalAttendeesField(fieldId, (field) => {
            const subFields = [...(field.additionalAttendeeFields || [])].sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0));
            const oldIndex = subFields.findIndex((subField) => subField.id === activeId);
            const newIndex = subFields.findIndex((subField) => subField.id === overId);
            if (oldIndex === -1 || newIndex === -1)
                return field;
            const reordered = arrayMove(subFields, oldIndex, newIndex).map((subField, index) => ({
                ...subField,
                fieldOrder: index + 1
            }));
            return {
                ...field,
                additionalAttendeeFields: reordered
            };
        });
    };
    const handleAdditionalAttendeeSubFieldOptionAdd = (fieldId, subFieldId) => {
        if (!canChangeStructure)
            return;
        updateAdditionalAttendeesField(fieldId, (field) => ({
            ...field,
            additionalAttendeeFields: field.additionalAttendeeFields?.map((subField) => {
                if (subField.id !== subFieldId)
                    return subField;
                const nextValue = (subField.options || []).reduce((max, option) => Math.max(max, option.value || 0), 0) + 1;
                return {
                    ...subField,
                    options: [
                        ...(subField.options || []),
                        {
                            id: crypto.randomUUID(),
                            label: `Option ${nextValue}`,
                            value: nextValue,
                            isFromBackend: false
                        }
                    ]
                };
            })
        }));
    };
    const handleAdditionalAttendeeSubFieldOptionRemove = (fieldId, subFieldId, optionId) => {
        if (!canChangeStructure)
            return;
        updateAdditionalAttendeesField(fieldId, (field) => ({
            ...field,
            additionalAttendeeFields: field.additionalAttendeeFields?.map((subField) => subField.id === subFieldId
                ? {
                    ...subField,
                    options: (subField.options || []).filter((option) => option.id !== optionId)
                }
                : subField)
        }));
    };
    const handleAdditionalAttendeeSubFieldOptionLabelUpdate = (fieldId, subFieldId, optionId, label) => {
        updateAdditionalAttendeesField(fieldId, (field) => ({
            ...field,
            additionalAttendeeFields: field.additionalAttendeeFields?.map((subField) => subField.id === subFieldId
                ? {
                    ...subField,
                    options: (subField.options || []).map((option) => option.id === optionId ? { ...option, label } : option)
                }
                : subField)
        }));
    };
    const handleCompanyDropdownFieldLabelUpdate = (fieldId, subFieldId, newLabel) => {
        // Allow label updates even in translation preview mode (when canChangeStructure is false)
        // Label editing should be allowed, only structure changes should be blocked
        const updated = formFields.map((f) => {
            if (f.id !== fieldId || f.fieldType !== 'companyDropdown')
                return f;
            // Validate: label cannot be empty
            const trimmedLabel = newLabel?.trim() || '';
            const isEmpty = trimmedLabel === '';
            return {
                ...f,
                companyDropdownFields: f.companyDropdownFields?.map((subField) => subField.id === subFieldId
                    ? {
                        ...subField,
                        label: newLabel,
                        error: isEmpty ? 'validations.required' : undefined,
                        errorField: isEmpty ? t('labels.fieldLabel') || 'Field label' : undefined
                    }
                    : subField)
            };
        });
        setValue(fieldPath, updated, {
            shouldDirty: true,
            shouldValidate: false
        });
    };
    function generateFieldConfig({ parentId, childId, parentLabel, childLabel, type, fieldOrder, descriptionForToolTip }) {
        const id = crypto.randomUUID();
        const optionId = crypto.randomUUID();
        const createToggleOptions = () => [
            {
                id: crypto.randomUUID(),
                value: 1,
                label: t('events.toggleOptions.yes') || 'Yes',
                isFromBackend: false
            },
            {
                id: crypto.randomUUID(),
                value: 0,
                label: t('events.toggleOptions.no') || 'No',
                isFromBackend: false
            }
        ];
        switch (type) {
            case 'text':
            case 'email':
            case 'phone':
            case 'date':
            case 'time':
            case 'companyDropdown':
                return {
                    id,
                    fieldParent: parentLabel,
                    fieldChild: childLabel,
                    fieldType: type,
                    label: t('events.formFieldsMenu.newField', { fieldName: childLabel ?? parentLabel }),
                    descriptionForToolTip,
                    required: false,
                    fieldOrder,
                    fieldAdditionalNote: '',
                    isFromBackend: false,
                    companyDropdownFields: [
                        { id: 'fullName', label: t('labels.fullName'), enabled: true, required: true },
                        { id: 'email', label: t('labels.email'), enabled: true, required: false },
                        { id: 'companyName', label: t('labels.companyName'), enabled: true, required: true }
                    ]
                };
            case 'radio':
            case 'choice':
            case 'checkbox-group':
            case 'dropdown':
            case 'multi-select':
                // These types require 'options'
                return {
                    id,
                    fieldParent: parentLabel,
                    fieldChild: childLabel,
                    fieldType: type,
                    label: t('events.formFieldsMenu.newField', { fieldName: childLabel ?? parentLabel }),
                    descriptionForToolTip,
                    required: false,
                    fieldOrder,
                    fieldAdditionalNote: '',
                    options: [{ id: optionId, value: 1, label: 'Option 1', isFromBackend: false }],
                    isFromBackend: false,
                    isBranchingEnabled: false,
                    optionalChaining: false,
                    associatedParent: ''
                };
            case 'multiline':
                return {
                    id,
                    fieldParent: parentLabel,
                    fieldChild: childLabel,
                    fieldType: 'multiline',
                    label: t('events.formFieldsMenu.newField', { fieldName: parentLabel }),
                    descriptionForToolTip,
                    required: false,
                    fieldOrder,
                    fieldAdditionalNote: '',
                    isFromBackend: false // Mark new fields as not from backend
                };
            case 'toggle':
                return {
                    id,
                    fieldParent: parentLabel,
                    fieldChild: childLabel,
                    fieldType: 'toggle',
                    label: t('events.formFieldsMenu.newToggle', { fieldName: parentLabel }),
                    descriptionForToolTip,
                    required: false,
                    fieldOrder,
                    fieldAdditionalNote: '',
                    options: createToggleOptions(),
                    isFromBackend: false,
                    isBranchingEnabled: false
                };
            case 'undertaking':
                return {
                    id,
                    fieldParent: parentLabel,
                    fieldChild: childLabel,
                    fieldType: 'undertaking',
                    label: t('events.undertaking.label'),
                    descriptionForToolTip,
                    required: true,
                    fieldOrder,
                    fieldAdditionalNote: '',
                    isFromBackend: false // Mark new fields as not from backend
                };
            case 'pickupLocation':
                return {
                    id,
                    fieldParent: parentLabel,
                    fieldChild: childLabel,
                    fieldType: 'pickupLocation',
                    label: t('events.formFieldsMenu.newField', { fieldName: childLabel ?? parentLabel }),
                    descriptionForToolTip,
                    required: false,
                    fieldOrder,
                    fieldAdditionalNote: '',
                    isFromBackend: false
                };
            // ... other cases
            default:
                return {
                    id,
                    fieldType: 'text',
                    label: t('events.formFieldsMenu.newField', { fieldName: 'Untitled' }),
                    descriptionForToolTip,
                    required: false,
                    fieldOrder,
                    fieldAdditionalNote: '',
                    isFromBackend: false // Mark new fields as not from backend
                };
        }
    }
    // Transport preset fields form one branching structure - if any of them has responses, lock
    // the whole group's reordering (including branch children no attendee has reached yet), since
    // reordering one field can break the saved branch order for the rest.
    const isTransportPresetGroupLocked = formFields.some((f) => (f.fieldAdditionalNote || '').startsWith('preset.transport.') && f.hasResponses);
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: {
                    position: 'sticky',
                    top: 0,
                    zIndex: theme.zIndex.appBar + 1,
                    borderRadius: '14px',
                    rowGap: 8,
                    padding: '12px',
                    gap: 1,
                    backgroundColor: (theme) => 'transparent',
                    border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.24)}`
                }, children: [_jsx(Typography, { variant: 'caption', color: theme.palette.grey[900], children: tStep('events.step', {
                            currentCount: currentStep || 0,
                            totalCount: totalSteps || 0
                        }) }), _jsx("input", { value: stepTitle ?? '', style: {
                            ...theme.typography.h5,
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            width: '100%',
                            padding: 0
                        }, onChange: (e) => {
                            setValue(stepNamePath, e.target.value, {
                                shouldDirty: true
                            });
                        }, onBlur: (e) => {
                            const trimmed = e.target.value?.trim() || '';
                            onStepNameError?.(trimmed === '');
                            setValue(stepNamePath, e.target.value, {
                                shouldValidate: true,
                                shouldTouch: true
                            });
                        } }), _jsx("input", { value: stepDescription ?? '', placeholder: t('eventSteps.defaultDesc'), style: {
                            ...theme.typography.body2,
                            color: theme.palette.text.secondary,
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            width: '100%',
                            padding: 0
                        }, onChange: (e) => {
                            setValue(stepDescriptionPath, e.target.value, {
                                shouldDirty: true
                            });
                        }, onBlur: (e) => {
                            setValue(stepDescriptionPath, e.target.value, {
                                shouldValidate: true,
                                shouldTouch: true
                            });
                        } })] }), (!stepTitle || stepTitle.trim() === '') && (_jsx(Typography, { variant: 'caption', sx: {
                    color: theme.palette.error.main,
                    ml: 2,
                    mt: 0.5,
                    display: 'block'
                }, children: t('validations.required', { field: t('events.createNewEvent.stepName') || 'Step name' }) })), _jsx(Box, { className: 'dynamic-form-container', sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    overflowX: 'clip',
                    overflowY: 'auto',
                    height: 'calc(100dvh - 248px)',
                    position: 'relative',
                    marginTop: 1,
                    paddingRight: 1,
                    // Hide scrollbar by default, show on hover
                    '&::-webkit-scrollbar': {
                        width: '6px',
                        height: '6px'
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'transparent',
                        borderRadius: '3px'
                    },
                    '&:hover::-webkit-scrollbar-thumb': {
                        background: 'rgba(0, 0, 0, 0.32)'
                    },
                    // Firefox scrollbar styling
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'transparent transparent',
                    '&:hover': {
                        scrollbarColor: 'rgba(0, 0, 0, 0.32) transparent'
                    },
                    // Hide scrollbar when dropdowns are open
                    '&:has(.MuiAutocomplete-popper, .MuiMenu-root, .MuiPopover-root)': {
                        '&::-webkit-scrollbar-thumb': {
                            background: 'transparent !important'
                        },
                        scrollbarColor: 'transparent transparent !important'
                    }
                }, children: _jsxs(Box, { sx: {
                        padding: isSmallScreen ? 1 : undefined,
                        pb: !isSmallScreen ? '60px' : undefined
                    }, children: [formFields.length === 0 && _jsx(WarningMessage, {}), _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, modifiers: [restrictToFirstScrollableAncestor, restrictToWindowEdges], children: _jsx(SortableContext, { items: formFields.map((f) => f.id), strategy: verticalListSortingStrategy, children: _jsx(Box, { sx: {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        overflowX: 'clip'
                                    }, children: formFields.map((field) => {
                                        // Get error for this field - translate if it's a key
                                        const fieldError = field.error && field.errorField
                                            ? t(field.error, { field: field.errorField }) || undefined
                                            : field.error;
                                        const claimedByOtherFields = new Set(formFields
                                            .filter((f) => f.id !== field.id)
                                            .flatMap((f) => f.options || [])
                                            .flatMap((opt) => opt.optionChainingShowField || [])
                                        // .filter((v) => v !== 'noAction')
                                        );
                                        // Ensure companyDropdownFields is initialized for company dropdown fields
                                        const companyFields = field.fieldType === 'companyDropdown' && !field.companyDropdownFields
                                            ? [
                                                {
                                                    id: 'fullName',
                                                    label: t('labels.fullName'),
                                                    enabled: true,
                                                    required: true
                                                },
                                                { id: 'email', label: t('labels.email'), enabled: true, required: false },
                                                {
                                                    id: 'companyName',
                                                    label: t('labels.companyName'),
                                                    enabled: true,
                                                    required: true
                                                }
                                            ]
                                            : field.companyDropdownFields;
                                        const additionalAttendeeFields = field.fieldType === ADDITIONAL_ATTENDEES_TYPE && !field.additionalAttendeeFields
                                            ? createDefaultAdditionalAttendeeSubFields((key) => String(t(key)))
                                            : field.additionalAttendeeFields;
                                        const maxAdditionalAttendees = field.fieldType === ADDITIONAL_ATTENDEES_TYPE
                                            ? field.maxAdditionalAttendees ?? 2
                                            : undefined;
                                        return (_jsx(SortableQuestionCard, { ...field, id: field.id, hasResponses: (field.fieldAdditionalNote || '').startsWith('preset.transport.')
                                                ? isTransportPresetGroupLocked
                                                : field.hasResponses, formFields: (() => {
                                                // Get all form fields from all steps to check for existing unique fields (email/phone/companyDropdown/Undertaking)
                                                const allSteps = watch('steps') || [];
                                                return allSteps.flatMap((step) => step.formFields || []);
                                            })(), parentOptionLabel: field.parentOptionLabel || parentOptionLabelMap[field.parentOptionId || ''], parentFieldLabel: field.parentFieldLabel ?? parentFieldLabelMap[field.parentOptionId || ''], canEdit: canChangeStructure, usedIdentityLabels: usedIdentityLabels, fieldType: field.fieldType, label: field.label, descriptionForToolTip: field.descriptionForToolTip, required: field.required, fieldParent: field.fieldParent, fieldChild: field.fieldChild, fieldOrder: field.fieldOrder, options: field.options, variant: field.variant, isFromBackend: field.isFromBackend, fileData: field.fileData, uploadedFileData: field.uploadedFileData, uploadedFilesByLanguage: field.undertakingUploadedFilesByLanguage, languages: selectedLanguages, undertakingToggleLabel: field.undertakingToggleLabel, undertakingAlertNote: field.undertakingAlertNote, error: fieldError, eventHasRegistration: eventHasRegistration, onFieldTypeChange: handleFieldTypeChange, onDuplicate: handleDuplicateField, onDelete: handleDeleteField, onToggleRequired: handleToggleRequired, onUpdateLabel: handleUpdateLabel, onUpdateDescriptionForTooltip: handleUpdateDescriptionForTooltip, onAddOption: handleAddOption, onUpdateOptionLabel: handleUpdateOptionLabel, onRemoveOption: handleRemoveOption, onOptionShowFieldChange: handleOptionShowFieldChange, isBranchingEnabled: field.isBranchingEnabled, onBranchingEnabledChange: handleBranchingEnabledChange, onFileChange: handleFileChange, onUndertakingFilesChange: handleUndertakingFilesChange, onUndertakingTextChange: handleUndertakingTextChange, companyDropdownFields: companyFields, onCompanyDropdownFieldToggle: handleCompanyDropdownFieldToggle, onCompanyDropdownFieldLabelUpdate: handleCompanyDropdownFieldLabelUpdate, maxAdditionalAttendees: maxAdditionalAttendees, additionalAttendeeFields: additionalAttendeeFields, maxAdditionalAttendeesReachedMessage: field.maxAdditionalAttendeesReachedMessage, onMaxAdditionalAttendeesChange: handleMaxAdditionalAttendeesChange, onAdditionalAttendeeSubFieldLabelUpdate: handleAdditionalAttendeeSubFieldLabelUpdate, onAdditionalAttendeeSubFieldTypeChange: handleAdditionalAttendeeSubFieldTypeChange, onAdditionalAttendeeSubFieldRequiredChange: handleAdditionalAttendeeSubFieldRequiredChange, onMaxAdditionalAttendeesReachedMessageChange: handleMaxAdditionalAttendeesReachedMessageChange, onAdditionalAttendeeSubFieldAdd: handleAdditionalAttendeeSubFieldAdd, onAdditionalAttendeeSubFieldRemove: handleAdditionalAttendeeSubFieldRemove, onAdditionalAttendeeSubFieldsReorder: handleAdditionalAttendeeSubFieldsReorder, onAdditionalAttendeeSubFieldOptionAdd: handleAdditionalAttendeeSubFieldOptionAdd, onAdditionalAttendeeSubFieldOptionRemove: handleAdditionalAttendeeSubFieldOptionRemove, onAdditionalAttendeeSubFieldOptionLabelUpdate: handleAdditionalAttendeeSubFieldOptionLabelUpdate, isExternalEvent: externalsAllowed, helperText: undefined, showField: field.showField, claimedFieldIds: claimedByOtherFields, stepQuestions: formFields
                                                .filter((f) => f.id !== field.id && (f.fieldOrder || 0) > (field.fieldOrder || 0))
                                                .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                                                .map((f) => ({ id: f.id, label: f.label })), onShowFieldChange: handleShowFieldChange }, field.id));
                                    }) }) }) }), _jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end' }, children: _jsxs(Box, { position: 'relative', mt: 1, children: [canChangeStructure && (_jsx(Button, { variant: 'white', color: 'primary', size: 'large', endIcon: _jsx(MoreHorizOutlinedIcon, { fontSize: 'small', sx: { color: theme.palette.primary.darker } }), className: 'add-question-menu-btn', sx: {
                                            color: theme.palette.primary.darker
                                        }, onClick: (e) => setAddQuestionBtnAnchorEl(e.currentTarget), children: t('actions.addQuestion') })), canChangeStructure && addQuestionBtnAnchorEl && (_jsx(FormFieldsMenu, { anchorRef: addQuestionBtnAnchorEl, anchorPosition: 'left', onClose: onClose, formFields: (() => {
                                            // Get all form fields from all steps to check for existing undertaking
                                            const allSteps = watch('steps') || [];
                                            return allSteps.flatMap((step) => step.formFields || []);
                                        })(), onSelect: ({ parent, child, type, description }) => {
                                            // Calculate fieldOrder for the new field
                                            const allSteps = watch('steps') || [];
                                            const currentStepIndex = allSteps.findIndex((s) => s.id === step.id);
                                            const fieldsInPreviousSteps = allSteps
                                                .slice(0, currentStepIndex)
                                                .reduce((total, s) => total + (s.formFields?.length || 0), 0);
                                            const newFieldOrder = fieldsInPreviousSteps + formFields.length + 1;
                                            if (type === TRANSPORTATION_PRESET_TYPE) {
                                                const transportationPresetFields = createTransportationPresetFields({
                                                    startingFieldOrder: newFieldOrder,
                                                    pickupLocations: (watch('pickupLocations') || []),
                                                    t: (key, options) => String(options ? t(key, options) : t(key))
                                                });
                                                setValue(fieldPath, [...formFields, ...transportationPresetFields]);
                                                return;
                                            }
                                            if (type === ADDITIONAL_ATTENDEES_TYPE) {
                                                const existingAdditionalAttendeesCount = formFields.filter((f) => f.fieldType === ADDITIONAL_ATTENDEES_TYPE).length;
                                                const additionalAttendeesField = createAdditionalAttendeesField({
                                                    fieldOrder: newFieldOrder,
                                                    existingCount: existingAdditionalAttendeesCount,
                                                    t: (key, options) => String(options ? t(key, options) : t(key))
                                                });
                                                setValue(fieldPath, [...formFields, additionalAttendeesField]);
                                                return;
                                            }
                                            const newField = generateFieldConfig({
                                                parentId: parent?.id,
                                                parentLabel: parent?.label,
                                                childId: child?.id,
                                                childLabel: child?.label,
                                                type,
                                                fieldOrder: newFieldOrder,
                                                descriptionForToolTip: description
                                            });
                                            setValue(fieldPath, [...formFields, newField]);
                                        } }))] }) })] }) }), _jsx(DeleteConfirmationDialog, { open: deleteConfirmationOpen, onClose: handleCancelDelete, onConfirm: handleConfirmDelete, fieldLabel: fieldToDelete?.label })] }));
};
export default DynamicForm;
